function getCompareProducts() {
  try {
    const data = JSON.parse(sessionStorage.getItem('compare-products') || '{}');
    return data && typeof data === 'object' && !Array.isArray(data) ? data : {};
  } catch (_) { return {}; }
}

if (!customElements.get('product-compare-checkbox')) customElements.define('product-compare-checkbox', class extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.input = this.querySelector('input');
    this.update = () => { this.input.checked = Boolean((document.querySelector('cq-compare-tray')?.products || getCompareProducts())[this.dataset.handle]); };
    this.input.addEventListener('change', () => document.querySelector('cq-compare-tray')?.select(this.dataset, this.input.checked));
    document.addEventListener('cq:compare-change', this.update);
    this.update();
  }
  disconnectedCallback() { document.removeEventListener('cq:compare-change', this.update); this.initialized = false; }
});

/* Compare bar and dialog (AJ r8 item 16, 2026-10-08).
   Each product's data comes from sections/product-compare-column.liquid. The dialog compares what matters:
   what each product reduces (a check where the product lists it, a dash where it does not), what it is best for
   and not designed for, its filtration, specs and options. Availability is gone. A row appears only when at least
   one product has a value, and "Show only differences" hides rows where every product matches.
   Data is fetched when a product is ticked, so the dialog opens complete (no growth after opening). */
// The site's one check mark (the cart's check), drawn at 16 px; dashes are a CSS bar, never a dash character.
const cqCompareCheck = () => {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24'); svg.setAttribute('class', 'cq-cmp-check'); svg.setAttribute('aria-hidden', 'true'); svg.setAttribute('focusable', 'false');
  svg.innerHTML = '<path d="M20 6 9 17l-5-5" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>';
  return svg;
};
/* No contradictory claims (AJ r8 item 16 review): custom.contaminants_reduced is shown as checks, but a check is never
   left plain where the product's own "Not designed for" line or its options say otherwise.
   - "See note", no check: the line says the product is not for removing or treating it ("PFAS/TDS removal",
     "bacteria/E. coli disinfection", "sulfur or rotten-egg odor", "chemical, sediment, or taste treatment").
   - Check with "With option": only some values of one option name it (Classic Under Sink's Arsenic cartridge).
   - Check with "See note": the line names it with a limit ("primary iron", "iron-heavy wells", "feed above 2,000 ppm TDS").
   - A product named for the contaminant (the Arsenic system) keeps its plain check; its line only sets limits.
   Matching is by word start ("soften" finds "softening"); a few contaminants go by other words. */
const cqCompareTerms = {
  pfas: ['pfas', 'pfoa', 'pfos', 'forever chemical'],
  tds: ['tds', 'total dissolved solids'],
  hardness: ['hardness', 'hard water', 'hard-water', 'soften', 'descal', 'limescale', 'scale buildup', 'scale build-up'],
  bacteria: ['bacteri', 'microb', 'coli', 'coliform', 'pathogen'],
  viruses: ['virus', 'viral', 'microb', 'bacteri', 'coli', 'coliform', 'pathogen'],
  'hydrogen sulfide': ['hydrogen sulfide', 'sulfur', 'sulphur', 'rotten egg', 'rotten-egg'],
};
const cqCompareTermPatterns = name => {
  const key = String(name || '').toLowerCase().trim();
  return (cqCompareTerms[key] || [key.replace(/s$/, '')]).filter(Boolean)
    .map(stem => new RegExp(`(^|[^a-z0-9])${stem.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}[a-z]*(?!-free)`, 'gi'));
};
const cqCompareMentions = (text, name) => Boolean(text) && cqCompareTermPatterns(name).some(re => { re.lastIndex = 0; return re.test(text); });
const cqCompareWithout = (text, name) => cqCompareTermPatterns(name).reduce((out, re) => out.replace(re, '$1 '), text);
const cqCompareDeny = /\b(remov(al|ed|e|ing)|reduc(tion|ed|e|ing)|treat(ment|ing)|disinfect(ion|ing)?|control|soften(ing|ed)|descal(ing|e)|odou?rs?|strip(ping)?)\b/i;
const cqCompareLimit = /\d|\b(ppm|above|below|beyond|over|feed|organic|naturally|without|consultation|saturated|outside|limits?|primary|main|alone|only|heavy|loaded|high|standalone|stand-alone|dedicated|specific|matching|basic|very)\b|-(heavy|loaded)\b|\blow-|\bpre-?treat/i;
const cqCompareWords = text => text.trim().split(/\s+/).filter(Boolean).length;
// 'deny', 'limit', 'mention' or '' for how a product's Not designed for line speaks about one contaminant.
const cqCompareNotFor = (text, name) => {
  let found = '';
  for (const clause of String(text || '').split(/[;:()]|\.(?=\s+[A-Z]|\s*$)/)) {
    const items = clause.split(/,|\/|\bor\b|\band\b/i);
    for (let index = 0; index < items.length; index += 1) {
      const item = items[index];
      if (!cqCompareMentions(item, name)) continue;
      if (cqCompareLimit.test(cqCompareWithout(item, name))) { found = found || 'limit'; continue; }
      // A qualifier just before a run of short list items governs them: "as a primary chlorine, hardness, or sediment filter".
      let back = index - 1;
      while (back >= 0 && cqCompareWords(items[back]) <= 2) back -= 1;
      if (back >= 0 && cqCompareLimit.test(items[back]) && cqCompareWords(item) <= 3) { found = found || 'limit'; continue; }
      if (cqCompareDeny.test(cqCompareWithout(item, name))) return 'deny';
      for (const next of items.slice(index + 1)) {
        if (cqCompareWords(next) > 4 || cqCompareLimit.test(next)) break;
        if (cqCompareDeny.test(next)) return 'deny';
      }
      found = found || 'mention';
    }
  }
  return found;
};

if (!customElements.get('cq-compare-tray')) customElements.define('cq-compare-tray', class extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.products = getCompareProducts();
    this.max = Number(this.dataset.maxProducts) || 5;
    this.columns = new Map();
    this.tray = this.querySelector('.cq-compare-tray');
    this.dialog = this.querySelector('dialog');
    this.content = this.querySelector('.cq-compare-content');
    this.status = this.querySelector('[data-compare-dialog-status]');
    this.strings = Object.fromEntries([...(this.querySelector('[data-compare-strings]')?.content.querySelectorAll('[data-key]') || [])]
      .map(el => [el.dataset.key, el.textContent.trim()]));
    try { this.diffOnly = sessionStorage.getItem('compare-diff-only') === '1'; } catch (_) { this.diffOnly = false; }
    this.openButton = this.querySelector('[data-compare-open]');
    this.openButton.addEventListener('click', () => this.open());
    // Warm any missing product data as soon as the shopper heads for Compare.
    this.openButton.addEventListener('pointerenter', () => this.prefetch(), { passive: true });
    this.openButton.addEventListener('focus', () => this.prefetch());
    this.querySelector('[data-compare-clear]').addEventListener('click', event => {
      const handle = Object.keys(this.products)[0];
      this.products = {}; this.update();
      // Keyboard and assistive tech (detail 0) get focus back on the first product's Compare box, since the bar hides.
      // A mouse or touch Clear leaves the page where it is; moving focus there scrolled the page back to that card.
      if (event.detail !== 0) return;
      [...document.querySelectorAll(`product-compare-checkbox[data-handle="${CSS.escape(handle || '')}"] input`)].find(el => el.getClientRects().length)?.focus();
    });
    this.querySelector('[data-compare-close]').addEventListener('click', () => this.close());
    this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(); });
    // However the dialog ends up closed, the page never stays scroll-locked (AJ 2026-10-09: stuck scroll after a popup).
    this.dialog.addEventListener('close', () => {
      window.cqScrollLock?.unlock(this);
      if (document.body.classList.contains('cq-compare-open')) {
        document.body.classList.remove('cq-compare-open');
        this.openButton?.setAttribute('aria-expanded', 'false');
      }
    });
    this.dialog.addEventListener('click', event => {
      if (event.target !== this.dialog) return;
      const rect = this.dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close();
    });
    this.dialog.addEventListener('keydown', event => {
      if (event.key !== 'Tab') return;
      const items = [...this.dialog.querySelectorAll('a[href],button:not(:disabled),input:not([type=hidden]):not(:disabled),select:not(:disabled),[tabindex="0"]')].filter(el => el.getClientRects().length && el.tabIndex !== -1);
      if (event.shiftKey && document.activeElement === items[0]) { event.preventDefault(); items.at(-1)?.focus(); }
      else if (!event.shiftKey && document.activeElement === items.at(-1)) { event.preventDefault(); items[0]?.focus(); }
    });
    this.content.addEventListener('click', event => {
      const remove = event.target.closest('[data-compare-remove]');
      if (remove) { this.removeFromDialog(remove.dataset.compareRemove); return; }
      const more = event.target.closest('[data-compare-more]');
      if (more) {
        const row = more.closest('tr');
        const open = row.classList.toggle('is-open');
        more.setAttribute('aria-expanded', String(open));
        more.textContent = open ? this.strings.less : this.strings.more;
      }
    });
    this.content.addEventListener('change', event => {
      if (event.target.matches('[data-compare-configuration]')) { this.changeConfiguration(event.target); return; }
      if (!event.target.matches('[data-compare-diff]')) return;
      this.diffOnly = event.target.checked;
      try { sessionStorage.setItem('compare-diff-only', this.diffOnly ? '1' : '0'); } catch (_) { /* Preference only. */ }
      this.applyDiff();
      this.status.textContent = this.diffOnly ? this.strings.diffOn : this.strings.diffOff;
    });
    this.onResize = () => { if (this.dialog.open) { cancelAnimationFrame(this.resizeFrame); this.resizeFrame = requestAnimationFrame(() => this.measure()); } };
    window.addEventListener('resize', this.onResize, { passive: true });
    this.cartHandler = event => {
      if (!this.dialog.open) return;
      this.close(false);
      event.detail.opener = this.openButton;
      const cart = document.querySelector('cq-cart-drawer');
      const focusCart = () => { if (cart?.isOpen && !cart.contains(document.activeElement)) cart.focusFirst?.(); };
      cart?.querySelector('.cq-cart-panel')?.addEventListener('transitionend', focusCart, { once: true });
      setTimeout(focusCart, 350);
    };
    document.addEventListener('cart-drawer:before-open', this.cartHandler);
    // Phones (AJ 2026-10-11): the bar sits on the bottom edge, so on a product page it steps aside (cq-compare-yield) while
    // the product's own Add to cart is on screen; the Configure popup and quick view hide it already.
    const buy = [...document.querySelectorAll('.cq-purchase-actions')].filter(el => !el.closest('dialog, quick-view-drawer'));
    if (buy.length && 'IntersectionObserver' in window) {
      const shown = new Set();
      this.buyObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => { if (entry.isIntersecting) shown.add(entry.target); else shown.delete(entry.target); });
        document.body.classList.toggle('cq-compare-yield', shown.size > 0);
      });
      buy.forEach(el => this.buyObserver.observe(el));
    }
    this.update();
  }
  disconnectedCallback() {
    document.removeEventListener('cart-drawer:before-open', this.cartHandler);
    window.removeEventListener('resize', this.onResize);
    this.buyObserver?.disconnect();
    document.body.classList.remove('cq-compare-yield', 'cq-compare-bar-on');
  }
  select(data, checked) {
    if (checked && !this.products[data.handle] && Object.keys(this.products).length >= this.max) {
      this.querySelector('[data-compare-status]').textContent = window.productCompareStrings.maxProductsAlert.replace('[max]', this.max);
    } else if (checked) {
      this.products[data.handle] = { id: data.id, handle: data.handle, title: data.title, url: data.url, image: data.image, variantId: this.products[data.handle]?.variantId };
      this.fetchColumn(this.products[data.handle]);
    } else delete this.products[data.handle];
    this.update();
  }
  // The bar's stylesheet loads off the first paint (snippets/card-product-compare-tray.liquid prints it with
  // media="print" and flips it on load), so a bar restored from sessionStorage waits for the sheet before it shows.
  revealTray(show) {
    if (!show) { this.tray.hidden = true; return; }
    const sheet = document.querySelector('link[data-cq-compare-css]');
    if (!sheet || sheet.media === 'all') { this.tray.hidden = false; return; }
    const reveal = () => { if (Object.keys(this.products).length) this.tray.hidden = false; };
    sheet.addEventListener('load', reveal, { once: true });
    sheet.addEventListener('error', reveal, { once: true });
  }
  update() {
    try { sessionStorage.setItem('compare-products', JSON.stringify(this.products)); } catch (_) { /* Selection still works without storage. */ }
    const products = Object.values(this.products).slice(0, this.max);
    this.revealTray(products.length > 0);
    document.body.classList.toggle('cq-compare-bar-on', products.length > 0);
    this.openButton.disabled = products.length < 2;
    this.querySelector('[data-compare-count]').textContent = products.length;
    if (this.dataset.countLabel) this.openButton.setAttribute('aria-label', this.dataset.countLabel.replace('[count]', products.length));
    const hint = this.querySelector('[data-compare-hint]');
    if (hint) hint.hidden = products.length !== 1;
    const thumbnails = this.querySelector('.cq-compare-thumbnails');
    thumbnails.replaceChildren(...products.map(product => {
      const button = document.createElement('button');
      button.type = 'button'; button.title = product.title;
      button.setAttribute('aria-label', this.dataset.remove.replace('[product]', product.title));
      if (product.image) {
        const img = document.createElement('img'); img.src = product.image; img.alt = ''; img.width = 44; img.height = 44;
        button.append(img);
      } else button.textContent = product.title.slice(0, 1);
      const remove = document.createElement('span'); remove.className = 'cq-compare-remove'; remove.setAttribute('aria-hidden', 'true');
      remove.innerHTML = '<svg viewBox="0 0 10 10" width="8" height="8" focusable="false"><path d="M1.5 1.5l7 7M8.5 1.5l-7 7" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';
      button.append(remove);
      button.addEventListener('click', event => {
        const index = Object.keys(this.products).indexOf(product.handle);
        delete this.products[product.handle]; this.update();
        const remaining = this.querySelectorAll('.cq-compare-thumbnails button');
        if (remaining.length) remaining[Math.min(index, remaining.length - 1)].focus();
        else if (event.detail === 0) [...document.querySelectorAll(`product-compare-checkbox[data-handle="${CSS.escape(product.handle)}"] input`)].find(el => el.getClientRects().length)?.focus();
      });
      return button;
    }));
    document.dispatchEvent(new CustomEvent('cq:compare-change'));
  }
  fetchColumn(product) {
    if (!product?.handle || !product.url) return Promise.resolve(null);
    const softenerCollection = document.querySelector('[data-softeners-v3]')
      && ['7021489671', '7088805575'].includes(String(product.id));
    const sectionId = softenerCollection ? 'cq-softeners-v3-compare' : 'product-compare-column';
    // Keep collection facts out of other pages, including after a section is removed in the editor.
    const cacheKey = `${sectionId}:${product.handle}`;
    if (!this.columns.has(cacheKey)) {
      const url = new URL(product.url, location.origin);
      url.searchParams.set('section_id', sectionId);
      // One quiet retry covers a brief storefront rate limit (429) or a dropped connection.
      const load = (retry = true) => fetch(url).then(response => {
        if (response.ok) return response.text();
        if (retry && (response.status === 429 || response.status >= 500)) return new Promise(resolve => setTimeout(resolve, 800)).then(() => load(false));
        throw new Error('Compare unavailable');
      }, error => {
        if (retry) return new Promise(resolve => setTimeout(resolve, 800)).then(() => load(false));
        throw error;
      });
      const request = load()
        .then(html => {
          const column = new DOMParser().parseFromString(html, 'text/html').querySelector('.cq-compare-column');
          if (!column) throw new Error('Compare missing');
          return column;
        })
        .catch(() => { this.columns.delete(cacheKey); return null; });
      this.columns.set(cacheKey, request);
    }
    return this.columns.get(cacheKey);
  }
  prefetch() { Object.values(this.products).slice(0, this.max).forEach(product => this.fetchColumn(product)); }
  loadShell() {
    // Configure owns the shell. Load its existing stylesheet on demand, once for either dialog.
    const url = this.dataset.shellCss;
    if (!url) return Promise.resolve(false);
    const existing = [...document.querySelectorAll('link[rel="stylesheet"]')].find(link => link.getAttribute('href') === url);
    if (existing?.sheet) return Promise.resolve(true);
    const link = existing || document.createElement('link');
    return new Promise(resolve => {
      const finish = loaded => {
        clearTimeout(timer);
        link.removeEventListener('load', onLoad);
        link.removeEventListener('error', onError);
        if (!loaded && !existing) link.remove();
        resolve(loaded);
      };
      const onLoad = () => finish(true);
      const onError = () => finish(false);
      // Notify Configure's loader too if it joined this request, so its retry never stays pending.
      const timer = setTimeout(() => link.dispatchEvent(new Event('error')), 15000);
      link.addEventListener('load', onLoad, { once: true });
      link.addEventListener('error', onError, { once: true });
      if (!existing) {
        link.rel = 'stylesheet'; link.href = url;
        document.head.append(link);
      }
    });
  }
  async open() {
    const products = Object.values(this.products).slice(0, this.max);
    if (products.length < 2 || this.loading) return;
    // Open once every column is in hand, so the dialog never grows or reflows after it appears.
    this.loading = true;
    this.openButton.setAttribute('aria-busy', 'true');
    const [shellReady, columns] = await Promise.all([this.loadShell(), Promise.all(products.map(product => this.fetchColumn(product)))]);
    this.loading = false;
    this.openButton.removeAttribute('aria-busy');
    if (!shellReady) {
      this.querySelector('[data-compare-status]').textContent = this.dataset.error;
      return;
    }
    if (this.dialog.open) return;
    this.items = products.map((product, index) => ({ product, data: this.read(columns[index], product) }));
    this.render();
    // r11 item 1: the shared scroll lock (assets/cq-scroll-lock.js). The page keeps its scrollbar (no empty strip at the
    // right edge) and no saved overflow value can be put back over another overlay's lock.
    window.cqScrollLock?.lock(this, { root: this.dialog, isOpen: () => this.dialog.open });
    document.body.classList.add('cq-compare-open');
    this.openButton.setAttribute('aria-expanded', 'true');
    this.dialog.showModal();
    this.content.scrollTo(0, 0);
    this.measure();
    this.querySelector('[data-compare-close]').focus();
  }
  close(returnFocus = true) {
    this.dialog.close();
    window.cqScrollLock?.unlock(this);
    document.body.classList.remove('cq-compare-open');
    this.openButton.setAttribute('aria-expanded', 'false');
    if (returnFocus) this.openButton.focus({ preventScroll: true });
  }
  read(column, product) {
    column = column?.cloneNode(true) || null;
    if (column) this.formatNumbers(column);
    const field = name => column?.querySelector(`[data-field="${name}"]`);
    const text = name => (field(name)?.textContent || '').replace(/\s+/g, ' ').trim();
    const list = name => {
      const seen = new Set();
      return [...(field(name)?.querySelectorAll('li') || [])].map(li => li.textContent.replace(/\s+/g, ' ').trim())
        .filter(value => value && !seen.has(value.toLowerCase()) && seen.add(value.toLowerCase()));
    };
    const data = {
      ok: Boolean(column),
      replacement: column?.hasAttribute('data-replacement') || false,
      kit: column?.hasAttribute('data-filter-kit') || false,
      head: column?.querySelector('[data-head]') || null,
      best: text('best'), not: text('not'),
      reduces: list('reduces'), noReduce: list('no-reduce'),
      tech: list('tech').join(', '),
      flow: text('flow'), capacity: text('capacity'), stages: text('stages'), install: text('install'), size: text('size'),
      media: list('media').join(', '), micron: text('micron'), production: text('production'), application: text('application'),
      options: [...(field('options')?.querySelectorAll('li') || [])].map(li => {
        let list = [];
        try { list = JSON.parse(li.dataset.values || '[]'); } catch (_) { list = []; }
        return { name: li.dataset.name || '', values: li.textContent.trim(), list: Array.isArray(list) ? list.map(String) : [] };
      }),
      title: product.title, url: product.url,
    };
    data.variants = [...(column?.querySelectorAll('template[data-compare-variant]') || [])].map(template => {
      const content = template.content.cloneNode(true);
      this.formatNumbers(content);
      const fact = key => (content.querySelector(`[data-field="${key}"]`)?.textContent || '').trim();
      return {
        id: template.dataset.compareVariant, title: template.dataset.title || '', url: template.dataset.url,
        available: template.dataset.available !== 'false',
        fields: Object.fromEntries(['sku', 'diameter', 'length', 'flow', 'capacity', 'capacityNote', 'interval', 'micron'].map(key => [key, fact(key)])),
        price: content.querySelector('[data-variant-price]'), image: content.querySelector('[data-variant-image] img'),
        action: content.querySelector('[data-variant-action]'),
      };
    });
    // A saved selection is used only when it still exists in the product's current variant set.
    if (data.variants.length) this.setVariant(data, product.variantId || column.dataset.selectedVariant);
    return data;
  }
  formatNumbers(root) {
    let format;
    try { format = new Intl.NumberFormat(document.documentElement.lang || 'en', { maximumFractionDigits: 20 }); }
    catch (_) { format = new Intl.NumberFormat('en', { maximumFractionDigits: 20 }); }
    root.querySelectorAll('[data-compare-number]').forEach(node => {
      const raw = node.dataset.compareNumber;
      if (raw !== '' && Number.isFinite(Number(raw))) node.textContent = format.format(Number(raw));
    });
  }
  setVariant(data, id) {
    const selected = data.variants.find(variant => variant.id === String(id)) || data.variants.find(variant => variant.available) || data.variants[0];
    if (!selected) return;
    data.selected = selected;
    // No sibling or product-level fallback: a Complete kit or an unmeasured inline cartridge must stay blank.
    Object.assign(data, selected.fields, { size: '' });
  }
  changeConfiguration(select) {
    const item = this.items.find(entry => entry.product.handle === select.dataset.compareConfiguration);
    if (!item || !item.data.variants.some(variant => variant.id === select.value)) return;
    this.setVariant(item.data, select.value);
    item.product.variantId = select.value;
    if (this.products[item.product.handle]) this.products[item.product.handle].variantId = select.value;
    this.update();
    const scroll = { left: this.content.scrollLeft, top: this.content.scrollTop };
    this.render();
    this.content.scrollTo(scroll);
    this.content.querySelector(`[data-compare-configuration="${CSS.escape(item.product.handle)}"]`)?.focus({ preventScroll: true });
    this.status.textContent = (this.strings.configurationChanged || '').replace('[product]', item.product.title).replace('[configuration]', item.data.selected.title);
  }
  productHead(data, product) {
    const head = document.importNode(data.head, true);
    const selected = data.selected;
    if (!selected) return head;
    head.querySelectorAll('.cq-cmp-tile, .js-product-link').forEach(link => { link.href = selected.url; });
    const tile = head.querySelector('.cq-cmp-tile');
    if (tile) tile.replaceChildren(...(selected.image ? [document.importNode(selected.image, true)] : []));
    let price = head.querySelector('.cq-cmp-price');
    if (!price && selected.price?.textContent.trim()) {
      price = this.el('p', 'cq-cmp-price');
      head.querySelector('.cq-cmp-name')?.after(price);
    }
    if (price && selected.price) { price.replaceChildren(...[...selected.price.childNodes].map(node => document.importNode(node, true))); price.hidden = !price.textContent.trim(); }
    if (selected.action) {
      const action = document.importNode(selected.action, true);
      const previous = head.querySelector('.cq-cmp-action');
      if (previous) previous.replaceWith(action);
      else head.append(action);
    }
    if (data.variants.length > 1) {
      const label = this.el('label', 'cq-cmp-configuration');
      label.append(this.el('span', null, this.strings.configuration));
      const select = this.el('select');
      select.dataset.compareConfiguration = product.handle;
      select.setAttribute('aria-label', (this.strings.configurationFor || '').replace('[product]', product.title));
      data.variants.forEach(variant => {
        const option = this.el('option', null, variant.title);
        option.value = variant.id; option.selected = variant.id === selected.id;
        select.append(option);
      });
      label.append(select);
      head.querySelector('.cq-cmp-head-text')?.append(label);
    }
    const details = this.el('a', 'cq-cmp-details', this.strings.fullDetails);
    details.href = selected.url;
    head.querySelector('.cq-cmp-head-text')?.append(details);
    return head;
  }
  el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }
  mark(state, values = []) {
    // A check where the product lists the contaminant, "No" where it says it does not, a dash where it lists nothing.
    // "With option": a check that holds only for the option values named. "See note": its Not designed for line limits
    // it (with a check) or excludes it (no check).
    const s = this.strings;
    const wrap = this.el('span', `cq-cmp-mark is-${state}`);
    const hidden = text => this.el('span', 'visually-hidden', text);
    const quiet = text => { const node = this.el('span', 'cq-cmp-qual', text); node.setAttribute('aria-hidden', 'true'); return node; };
    if (state === 'yes') wrap.append(cqCompareCheck(), hidden(s.reduces));
    else if (state === 'option') {
      const list = values.join(', ');
      wrap.title = list;
      wrap.append(cqCompareCheck(), quiet(s.withOption), hidden((s.withOptionSr || '').replace('[options]', list)));
    } else if (state === 'limit') wrap.append(cqCompareCheck(), quiet(s.seeNote), hidden(s.limitSr));
    else if (state === 'note') wrap.append(quiet(s.seeNote), hidden(s.seeNoteSr));
    else if (state === 'no') wrap.append(this.el('span', 'cq-cmp-no', s.no), hidden(s.doesNotReduce));
    else wrap.append(this.el('span', 'cq-cmp-dash'), hidden(s.notListed));
    wrap.firstChild.setAttribute('aria-hidden', 'true');
    return wrap;
  }
  reduceState(data, key, name) {
    // Fail closed: an explicit "does not reduce" beats everything, then the product's own Not designed for line,
    // then option scope. assets/product-compare.js header (cqCompareNotFor) explains each mark.
    if (data.noReduce.some(value => value.toLowerCase() === key)) return { state: 'no' };
    if (!data.reduces.some(value => value.toLowerCase() === key)) return { state: 'none' };
    const named = cqCompareMentions(data.title, name);
    const notFor = named ? '' : cqCompareNotFor(data.not, name);
    if (notFor === 'deny') return { state: 'note' };
    for (const option of data.options) {
      const values = option.list.filter(value => cqCompareMentions(value, name));
      if (values.length && values.length < option.list.length) return { state: 'option', values };
    }
    if (notFor) return { state: 'limit' };
    return { state: 'yes' };
  }
  render() {
    const s = this.strings;
    const items = this.items;
    const n = items.length;
    this.dialog.style.setProperty('--n', n);
    const table = this.el('table', 'cq-cmp-table');
    table.style.setProperty('--n', n);
    table.append(this.el('caption', 'visually-hidden', (s.caption || '').replace('[count]', n)));

    // Sticky product heads; the corner holds the differences switch and the swipe hint.
    const head = this.el('thead');
    const headRow = this.el('tr');
    const corner = this.el('td', 'cq-cmp-corner');
    const diff = this.el('label', 'cq-cmp-diff');
    const box = this.el('input'); box.type = 'checkbox'; box.dataset.compareDiff = ''; box.checked = this.diffOnly;
    diff.append(box, this.el('span', null, s.diff));
    this.hint = this.el('p', 'cq-cmp-hint'); this.hint.hidden = true;
    corner.append(diff, this.hint);
    headRow.append(corner);
    items.forEach(({ product, data }) => {
      const cell = this.el('th', 'cq-cmp-product'); cell.scope = 'col';
      if (data.head) cell.append(this.productHead(data, product));
      else {
        // The product could not load: keep its name and a way to its page.
        const fallback = this.el('div', 'cq-cmp-head');
        const text = this.el('div', 'cq-cmp-head-text');
        const link = this.el('a', 'cq-cmp-name', product.title); link.href = product.url;
        text.append(link, this.el('p', 'cq-cmp-error', this.dataset.error));
        fallback.append(text);
        cell.append(fallback);
      }
      const remove = this.el('button', 'cq-cmp-remove');
      remove.type = 'button'; remove.dataset.compareRemove = product.handle;
      remove.setAttribute('aria-label', this.dataset.remove.replace('[product]', product.title));
      const glyph = this.el('span', null, '×'); glyph.setAttribute('aria-hidden', 'true'); remove.append(glyph);
      cell.append(remove);
      headRow.append(cell);
    });
    head.append(headRow);
    table.append(head);
    // When every product shares one category, its label in each head is noise; hide it.
    const types = [...headRow.querySelectorAll('.cq-card-type')].map(type => type.textContent.trim());
    if (types.length === n && types.every(type => type === types[0])) table.classList.add('is-same-type');

    const same = values => values.every(value => value === values[0]);
    const addRow = (body, key, label, values, { clamp = false } = {}) => {
      const keys = values.map(value => (typeof value === 'string' ? value : value?.key) || '');
      if (keys.every(value => !value)) return;
      const row = this.el('tr', `cq-cmp-row cq-cmp-row-${key}`);
      if (same(keys)) row.dataset.same = '';
      if (clamp) row.dataset.clamp = '';
      const th = this.el('th'); th.scope = 'row';
      th.append(this.el('span', 'cq-cmp-label', label));
      row.append(th);
      values.forEach((value, index) => {
        const td = this.el('td');
        // A product that failed to load says so in its head; its cells stay blank rather than claim "Not listed".
        if (!items[index].data.ok) { row.append(td); return; }
        if (!keys[index]) td.append(this.el('span', 'cq-cmp-empty', s.notListed));
        else if (value?.node) td.append(value.node);
        else td.append(this.el('div', clamp ? 'cq-cmp-text is-clamped' : 'cq-cmp-text', value));
        row.append(td);
      });
      body.append(row);
    };
    const group = (label, legend) => {
      const body = this.el('tbody', 'cq-cmp-group');
      if (label) {
        const row = this.el('tr', 'cq-cmp-group-head');
        const th = this.el('th'); th.scope = 'rowgroup'; th.colSpan = n + 1;
        const inner = this.el('div', 'cq-cmp-group-inner');
        inner.append(this.el('span', 'cq-cmp-eyebrow', label));
        if (legend) inner.append(legend);
        th.append(inner); row.append(th); body.append(row);
      }
      return body;
    };

    // 1. Who each product is for.
    const intro = group(null);
    addRow(intro, 'best', s.best, items.map(({ data }) => data.best), { clamp: true });
    addRow(intro, 'not', s.not, items.map(({ data }) => data.not), { clamp: true });

    // 2. What it reduces, from each product's own contaminant list.
    const lower = value => value.toLowerCase();
    const withData = items.filter(({ data }) => data.reduces.length || data.noReduce.length);
    const legend = this.el('span', 'cq-cmp-legend');
    legend.setAttribute('aria-hidden', 'true');
    legend.append(cqCompareCheck(), ` ${s.reduces}`, this.el('span', 'cq-cmp-dash'), ` ${s.notListed}`);
    const reduces = group(s.reducesHeading, legend);
    if (withData.length) {
      const names = new Map();
      items.forEach(({ data }) => [...data.reduces, ...data.noReduce].forEach(name => { if (!names.has(lower(name))) names.set(lower(name), name); }));
      const states = new Map([...names.keys()].map(key => [key, items.map(({ data }) => this.reduceState(data, key, names.get(key)))]));
      const shared = withData.length === n ? [...names.keys()].filter(key => states.get(key).every(cell => cell.state === 'yes')) : [];
      if (shared.length) {
        const row = this.el('tr', 'cq-cmp-row cq-cmp-row-shared');
        row.dataset.same = '';
        const th = this.el('th'); th.scope = 'row';
        th.append(this.el('span', 'cq-cmp-label', n === 2 ? s.sharedTwo : (s.sharedAll || '').replace('[count]', n)));
        const td = this.el('td'); td.colSpan = n;
        td.append(this.el('div', 'cq-cmp-shared', shared.map(key => names.get(key)).sort((a, b) => a.localeCompare(b)).join(', ')));
        row.append(th, td);
        reduces.append(row);
      }
      [...names.keys()].filter(key => !shared.includes(key)).sort((a, b) => names.get(a).localeCompare(names.get(b))).forEach(key => {
        addRow(reduces, 'reduce', names.get(key), states.get(key).map(cell => ({ key: cell.state, node: this.mark(cell.state, cell.values) })));
      });
    }

    // 3. Details.
    const details = group(s.detailsHeading, items.some(({ data }) => data.replacement && !data.kit) ? this.el('span', 'cq-cmp-spec-note', s.perCartridge) : null);
    addRow(details, 'tech', s.tech, items.map(({ data }) => data.tech));
    ['sku', 'diameter', 'length', 'size', 'flow', 'production', 'application', 'capacity', 'interval', 'media', 'micron', 'stages', 'install'].forEach(key => {
      addRow(details, key, s[key], items.map(({ data }) => {
        if (key !== 'capacity' || !data.capacityNote) return data[key];
        const node = this.el('div', 'cq-cmp-text');
        if (data.capacity) node.append(this.el('div', null, data.capacity));
        node.append(this.el('p', 'cq-cmp-spec-note', data.capacityNote));
        return { key: `${data.capacity || ''}|${data.capacityNote}`, node };
      }));
    });
    addRow(details, 'options', s.options, items.map(({ data }) => {
      if (!data.options.length) return '';
      const node = this.el('div', 'cq-cmp-text is-clamped');
      data.options.forEach(option => {
        const line = this.el('p', 'cq-cmp-option');
        line.append(this.el('strong', null, option.name), ` ${option.values}`);
        node.append(line);
      });
      return { key: data.options.map(option => `${option.name}:${option.values}`).join('|'), node };
    }), { clamp: true });

    [intro, reduces, details].forEach(body => {
      const rows = body.querySelectorAll('.cq-cmp-row');
      if (!rows.length) return;
      if ([...rows].every(row => row.hasAttribute('data-same'))) body.dataset.same = '';
      table.append(body);
    });
    const none = this.el('tbody', 'cq-cmp-none');
    const noneRow = this.el('tr'); const noneCell = this.el('td', null, s.noDiff); noneCell.colSpan = n + 1;
    noneRow.append(noneCell); none.append(noneRow); table.append(none);
    this.table = table;
    this.content.replaceChildren(table);
    this.applyDiff();
  }
  applyDiff() {
    if (!this.table) return;
    this.table.classList.toggle('is-diff-only', this.diffOnly);
    const visible = [...this.table.querySelectorAll('.cq-cmp-row')].some(row => !row.hasAttribute('data-same'));
    this.table.classList.toggle('is-all-same', !visible);
    this.measure();
  }
  measure() {
    if (!this.table || !this.dialog.open) return;
    // Heads share one text height so every Add to cart / Configure button sits on the same line.
    const texts = [...this.table.querySelectorAll('thead .cq-cmp-head-text')];
    texts.forEach(text => { text.style.minHeight = ''; });
    const tallest = Math.max(0, ...texts.map(text => text.offsetHeight));
    texts.forEach(text => { text.style.minHeight = `${tallest}px`; });
    // Keyboard scrolling must reveal row controls below the pinned product heads.
    this.content.style.setProperty('--compare-head-height', `${this.table.tHead.offsetHeight}px`);
    // "Show more" appears only on rows whose text is actually cut off at this width.
    this.table.querySelectorAll('tr[data-clamp]').forEach(row => {
      const th = row.querySelector('th');
      let button = th.querySelector('[data-compare-more]');
      const open = row.classList.contains('is-open');
      const cut = open || [...row.querySelectorAll('.cq-cmp-text.is-clamped')].some(text => text.scrollHeight > text.clientHeight + 1);
      if (cut && !button) {
        button = this.el('button', 'cq-cmp-more', open ? this.strings.less : this.strings.more);
        button.type = 'button'; button.dataset.compareMore = ''; button.setAttribute('aria-expanded', String(open));
        th.append(button);
      } else if (!cut && button) button.remove();
    });
    // The hint follows the columns themselves: a hit area reaching past a cell edge must never read as "more to see".
    const overflow = Math.ceil(this.table.getBoundingClientRect().width) > this.content.clientWidth + 1;
    if (this.hint) {
      this.hint.hidden = !overflow;
      const coarse = window.matchMedia('(pointer: coarse)').matches;
      this.hint.textContent = ((coarse ? this.strings.swipe : this.strings.scroll) || '').replace('[count]', this.items.length);
    }
  }
  removeFromDialog(handle) {
    const index = this.items.findIndex(item => item.product.handle === handle);
    if (index < 0) return;
    delete this.products[handle];
    this.update();
    this.items.splice(index, 1);
    if (this.items.length < 2) {
      this.close(false);
      (this.querySelector('.cq-compare-thumbnails button') || this.openButton).focus({ preventScroll: true });
      return;
    }
    this.render();
    this.measure();
    const removes = this.content.querySelectorAll('[data-compare-remove]');
    (removes[Math.min(index, removes.length - 1)] || this.querySelector('[data-compare-close]')).focus();
  }
});
if (!customElements.get("product-compare")) {
  customElements.define(
    "product-compare",
    class ProductCompare extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.dataset.sectionId;
        this.container = this.querySelector(".product-compare-grid");
        this.handleDismiss = this.handleDismissClick.bind(this);
      }

      async connectedCallback() {
        this.updateColumnCount();
        await this.loadComparisonProducts();

        if (!Shopify.designMode) {
          this.removeEmptyLines();
        } else {
          this.classList.add("is-loaded");
        }
        this.container.addEventListener("click", this.handleDismiss);
        this.updateDismissButtonVisibility();
      }

      disconnectedCallback() {
        this.container.removeEventListener("click", this.handleDismiss);
      }

      handleDismissClick(event) {
        const dismissButton = event.target.closest(".product-dismiss-button");
        if (!dismissButton) {
          return;
        }

        const productColumn = dismissButton.closest(".product-compare-product");
        const handle = productColumn?.dataset.handle;

        if (!handle || !productColumn) {
          return;
        }

        try {
          let products = getCompareProducts();
          if (handle in products) {
            delete products[handle];
            sessionStorage.setItem("compare-products", JSON.stringify(products));
          }
        } catch (e) {
          console.error("Failed to update session storage on dismiss:", e);
        }

        publish(PUB_SUB_EVENTS.compareRemove, { detail: { handle: handle } });
        productColumn.remove();

        this.updateColumnCount();
        this.updateDismissButtonVisibility();
        if (!Shopify.designMode) {
          this.removeEmptyLines();
        }

        const modal = this.closest("product-compare-modal");
        const remainingProducts = this.container.querySelectorAll(
          ".product-compare-product",
        ).length;

        if (modal && remainingProducts <= 1) {
          modal.hide();
        }
      }

      updateColumnCount() {
        const productCols = this.container.querySelectorAll(".product-compare-product");
        this.container.style.setProperty("--compare-product-count", productCols.length);
      }

      updateDismissButtonVisibility() {
        const productCols = this.container.querySelectorAll(".product-compare-product");
        const dismissButtons = this.container.querySelectorAll(".product-dismiss-button");
        const showButtons = productCols.length > 1;

        dismissButtons.forEach((button) => {
          button.hidden = !showButtons;
        });
      }

      removeEmptyLines() {
        const linesCount = Number(this.container.style.getPropertyValue("--compare-row-count"));
        if (!linesCount || linesCount === 0) {
          return;
        }

        this.container.querySelectorAll(".product-compare-value").forEach((cell) => {
          cell.dataset.isEmpty =
            cell.innerText.trim() === "" &&
            cell.querySelectorAll("img, iframe, video").length === 0;
        });

        for (let i = 1; i <= linesCount; i++) {
          const cellsInRow = this.container.querySelectorAll(
            `.product-compare-item-row[data-index="${i}"]`,
          );
          if (cellsInRow.length === 0) continue;

          let rowHasContent = false;
          cellsInRow.forEach((cell) => {
            if (cell.classList.contains("product-compare-label")) {
              // Do thing. The label shouldn't determine whether the values will be shown.
            } else if (cell.classList.contains("product-compare-value")) {
              if (cell.dataset.isEmpty !== "true") {
                rowHasContent = true;
              }
            } else {
              if (cell.innerText.trim() !== "") {
                rowHasContent = true;
              }
            }
          });

          if (!rowHasContent) {
            cellsInRow.forEach((cell) => cell.classList.add("compare-row-hidden"));
          } else {
            cellsInRow.forEach((cell) => cell.classList.remove("compare-row-hidden"));
          }
        }
      }

      async loadComparisonProducts() {
        let productHandles = [];

        if (this.dataset.handles) {
          try {
            productHandles = JSON.parse(this.dataset.handles);
          } catch (e) {
            productHandles = [];
          }
        } else {
          const urlParams = new URLSearchParams(window.location.search);
          productHandles = urlParams.getAll("handles");
        }

        if (!productHandles.length) {
          this.updateColumnCount();
          this.classList.add("is-loaded");

          if (!Shopify.designMode) {
            this.removeEmptyLines();
          }

          return;
        }

        this.querySelectorAll(".product-compare-item-preview").forEach((element) => {
          element.remove();
        });

        let baseProductPath = "/products";
        if (window.routes.root_url !== "/") {
          baseProductPath = window.routes.root_url + "/products";
        }

        const fetchPromises = productHandles.map((handle) => {
          const url = new URL(`${baseProductPath}/${handle}`, window.shopUrl);
          url.searchParams.set("view", "compare");
          url.searchParams.set("section_id", this.sectionId);
          return fetch(url.href.replace(window.shopUrl, ""));
        });

        try {
          const responses = await Promise.all(fetchPromises);

          const textPromises = responses.map((response) => {
            if (!response.ok) {
              console.error(`Failed to load product: ${response.url}, Status: ${response.status}`);
              return Promise.resolve(null);
            }
            return response.text();
          });

          const htmlContents = await Promise.all(textPromises);

          const productElements = htmlContents
            .map((html) => {
              if (!html) {
                return null;
              }

              const parser = new DOMParser();
              const doc = parser.parseFromString(html, "text/html");
              return doc.querySelector(".product-compare-product");
            })
            .filter((element) => element !== null);

          productElements.forEach((productElement) => {
            this.container.appendChild(productElement);
          });
        } catch (error) {
          console.error("Error processing comparison products:", error);
        }

        this.updateColumnCount();

        if (!Shopify.designMode) {
          this.removeEmptyLines();
        } else {
          this.hidePreviewIfEnoughProducts();
        }

        this.classList.add("is-loaded");
      }

      hidePreviewIfEnoughProducts() {
        // In design mode, hide the placeholder if there are 2 or more real products
        const realProducts = this.container.querySelectorAll(
          ".product-compare-product:not(.product-compare-item-preview)",
        );
        const placeholder = this.container.querySelector(".product-compare-item-preview");

        if (placeholder && realProducts.length >= 2) {
          placeholder.style.display = "none";
          this.container.style.setProperty(
            "--compare-product-count",
            realProducts.length.toString(),
          );
        }
      }
    },
  );
}

// Round 3 (A1-11): away from collection and search pages this file loads on demand (snippets/card-product-compare-tray.liquid).
// Compare boxes ticked before it arrived are replayed here, in the same run that defined the elements, so no tick is lost.
window.cqCompareDrain?.();
